import type {
  CreatePaymentBody,
  CreatePaymentLine,
  PaymentSource,
  ReceiptItemAssignment,
  TaxCategoryOption,
} from '@pet/shared';

// A single wizard line draft. `deMinimisEligible` holds the CURRENT effective value
// (pre-filled from the chosen category's default, then user-overridable). `null` means "no
// category chosen yet" — such a line is not assemblable. The capital flag is not a draft field:
// the category decides it, and the server derives it (AD-10, amended 2026-09-23). Money is
// integer cents.
export interface LineDraft {
  id: string; // stable React key; never sent to the server
  propertyId: string;
  taxCategoryKey: string;
  amountCents: number | null;
  description: string;
  deMinimisEligible: boolean | null;
}

export interface PaymentDraft {
  date: string;
  payee: string;
  paymentMethod: string;
  paymentSource: PaymentSource | '';
  ownerId: string;
  lines: LineDraft[];
  // AD-18 — each is copied into the body only when present, so a draft without them builds
  // exactly the body it always did.
  memo?: string;
  receiptIngestionId?: string;
  receiptTotalCents?: number;
  // AD-18 Build C — the item → line mapping, set only when the lines came from tapped items.
  receiptItemAllocation?: ReceiptItemAssignment[];
}

export type BuildResult =
  | { ok: true; body: CreatePaymentBody }
  | { ok: false; error: string };

// Whether a line's effective de minimis flag differs from the chosen category's default.
// The UI uses this to render a "you changed it" affordance; assembly uses it to decide
// whether to send the explicit boolean (override) or omit it (accept server default).
export function isDeMinimisOverridden(
  line: Pick<LineDraft, 'taxCategoryKey' | 'deMinimisEligible'>,
  categories: TaxCategoryOption[]
): boolean {
  const cat = categories.find((c) => c.key === line.taxCategoryKey);
  if (!cat || line.deMinimisEligible === null) return false;
  return line.deMinimisEligible !== cat.deMinimisEligibleDefault;
}

// Pure assembly of the POST /api/payments `payload`. This is the load-bearing money +
// category-override (AD-10) logic, unit-tested directly:
//   - cents are asserted positive integers (never floats)
//   - LLC_FUNDED omits ownerId entirely; OWNER_FUNDED includes it
//   - per-line deMinimisEligible is OMITTED when it still matches the chosen category's
//     default, and SENT as an explicit boolean only when the human overrode it
//   - the capital flag is never sent: the server derives it from the category
//   - line order is preserved
export function buildPaymentPayload(
  draft: PaymentDraft,
  categories: TaxCategoryOption[]
): BuildResult {
  if (!draft.date) {
    return { ok: false, error: 'Please choose a date.' };
  }
  if (draft.payee.trim().length === 0) {
    return { ok: false, error: 'Please enter who this payment was made to.' };
  }
  if (draft.paymentSource !== 'LLC_FUNDED' && draft.paymentSource !== 'OWNER_FUNDED') {
    return { ok: false, error: 'Please choose who paid.' };
  }
  if (draft.paymentSource === 'OWNER_FUNDED' && draft.ownerId.length === 0) {
    return { ok: false, error: 'Please choose which owner paid.' };
  }
  if (draft.lines.length === 0) {
    return { ok: false, error: 'Please add at least one line.' };
  }

  const lines: CreatePaymentLine[] = [];
  for (let i = 0; i < draft.lines.length; i++) {
    const d = draft.lines[i];
    if (!d.propertyId) {
      return { ok: false, error: `Line ${i + 1}: please choose a property.` };
    }
    if (!d.taxCategoryKey) {
      return { ok: false, error: `Line ${i + 1}: please choose a category.` };
    }
    const cat = categories.find((c) => c.key === d.taxCategoryKey);
    if (!cat) {
      return { ok: false, error: `Line ${i + 1}: that category isn't recognized.` };
    }
    if (d.amountCents === null || !Number.isInteger(d.amountCents) || d.amountCents <= 0) {
      return { ok: false, error: `Line ${i + 1}: please enter a valid amount.` };
    }

    const line: CreatePaymentLine = {
      propertyId: d.propertyId,
      taxCategoryKey: d.taxCategoryKey,
      amountCents: d.amountCents,
    };
    if (d.description.trim().length > 0) {
      line.description = d.description.trim();
    }
    // AD-10: send the explicit boolean only when it differs from the category default.
    if (
      d.deMinimisEligible !== null &&
      d.deMinimisEligible !== cat.deMinimisEligibleDefault
    ) {
      line.deMinimisEligible = d.deMinimisEligible;
    }
    lines.push(line);
  }

  const body: CreatePaymentBody = {
    date: draft.date,
    payee: draft.payee.trim(),
    paymentSource: draft.paymentSource,
    lines,
  };
  if (draft.paymentMethod.trim().length > 0) {
    body.paymentMethod = draft.paymentMethod.trim();
  }
  // LLC_FUNDED must NOT carry ownerId (server 400s on it). Include only when owner-funded.
  if (draft.paymentSource === 'OWNER_FUNDED') {
    body.ownerId = draft.ownerId;
  }
  if (draft.memo !== undefined) {
    body.memo = draft.memo;
  }
  // A receipt total is only meaningful with its receipt; the server 400s one without the other.
  if (draft.receiptIngestionId !== undefined) {
    body.receiptIngestionId = draft.receiptIngestionId;
    if (draft.receiptTotalCents !== undefined) {
      body.receiptTotalCents = draft.receiptTotalCents;
    }
    // The mapping, too, is only meaningful with its receipt (the server 400s it without one).
    if (draft.receiptItemAllocation !== undefined) {
      body.receiptItemAllocation = draft.receiptItemAllocation;
    }
  }

  return { ok: true, body };
}

export function totalCents(lines: LineDraft[]): number {
  return lines.reduce((sum, l) => sum + (l.amountCents ?? 0), 0);
}
